import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import { ConfirmTypes } from '@/components';
import { baseURL } from '@/config';
import { store } from '@/store';
import { logout } from '@/store/authSlice';
import { showToast } from '@/store/toastSlice';

enum StatusCode {
  Unauthorized = 401,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
}

const headers: Readonly<Record<string, string | boolean>> = {
  Accept: 'application/json',
  'Content-Type': 'application/json; charset=utf-8',
  'Access-Control-Allow-Credentials': true,
  'X-Requested-With': 'XMLHttpRequest',
};

class Http {
  private instance: AxiosInstance | null = null;
  private isLoggingOut = false;

  private get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.initHttp();
  }

  initHttp() {
    const http = axios.create({
      baseURL,
      headers,
      withCredentials: true,
    });

    http.interceptors.response.use(
      (response) => response,
      async (error) => {
        const { response } = error;
        return await this.handleError(response);
      },
    );

    this.instance = http;
    return http;
  }

  async request<T = any, R = AxiosResponse<T>>(
    config: AxiosRequestConfig,
  ): Promise<R> {
    return await this.http.request(config);
  }

  async get<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return await this.http.get<T, R>(url, config);
  }

  async post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return await this.http.post<T, R>(url, data, config);
  }

  async put<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return await this.http.put<T, R>(url, data, config);
  }

  async delete<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return await this.http.delete<T, R>(url, config);
  }

  private async handleError(error: AxiosError) {
    const { status, data } = error as AxiosError & {
      data?: { message?: string; details?: string[] };
    };

    switch (status) {
      case StatusCode.InternalServerError: {
        // Handle InternalServerError
        break;
      }
      case StatusCode.Forbidden:
      case StatusCode.Unauthorized: {
        if (!this.isLoggingOut) {
          this.isLoggingOut = true;
          store.dispatch(logout());
          this.setToken(null);

          const message =
            data?.message?.toLowerCase() === 'unauthorized'
              ? 'Access denied. You are not authorized to access this application.'
              : 'Your session has expired. Please log in again.';

          store.dispatch(
            showToast({
              type: ConfirmTypes.ERROR,
              message,
            }),
          );
          setTimeout(() => {
            this.isLoggingOut = false;
          }, 2000);
        }
        break;
      }
      case StatusCode.TooManyRequests: {
        // Handle TooManyRequests
        break;
      }
    }

    return await Promise.reject(error);
  }

  setToken(accessToken: string | null) {
    if (this.instance == null) {
      this.initHttp();
    }
    if (this.instance != null) {
      const newToken = `Bearer ${accessToken}`;
      const currentHeaders = this.instance.defaults.headers.common;
      if (newToken !== currentHeaders.Authorization) {
        this.instance.defaults.headers.common = {
          ...axios.defaults.headers.common,
          Authorization: newToken,
        };
      }
    }
  }

  setTenant(subdomain?: string | null) {
    if (this.instance != null) {
      this.instance.defaults.headers.common = {
        ...this.instance.defaults.headers.common,
        'X-HELPTAPP-Account': subdomain,
      };
    }
  }
}

export const http = new Http();
